import * as Sentry from "@sentry/react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { FormEvent, useState, JSX } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  ButtonVariant,
  Checkbox,
  DrawerActions,
  DrawerContent,
} from "@industriousoffice/react-components";

type StripeAddPaymentMethodFormProps = {
  clientSecret: string;
  returnUrl: string;
  isDefaultPaymentChecked: boolean;
  isFirstPaymentMethod?: boolean;
  setIsDefaultPaymentChecked: (checked: boolean) => void;
  paymentMethodInformation?: () => JSX.Element;
  submitLabel?: string;
};

export default function StripeAddPaymentMethodForm({
  clientSecret,
  returnUrl,
  isDefaultPaymentChecked,
  isFirstPaymentMethod,
  setIsDefaultPaymentChecked,
  submitLabel,
  paymentMethodInformation,
}: StripeAddPaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation("paymentMethods");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<boolean>(false);

  const handleSubmit = async (event: FormEvent) => {
    setIsLoading(true);
    event.preventDefault();
    try {
      if (!stripe) {
        setPaymentError(true);
        return;
      }

      await elements?.submit();

      const payment = await stripe.confirmSetup({
        clientSecret,
        elements: elements!,
        confirmParams: {
          return_url: `${returnUrl}?isDefault=${isDefaultPaymentChecked}`,
        },
      });

      if (payment.error) {
        Sentry.captureException(payment.error);
        setPaymentError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      className="flex flex-col h-full mt-5 overflow-y-auto"
      onSubmit={handleSubmit}
    >
      <DrawerContent>
        <PaymentElement />

        {paymentMethodInformation && paymentMethodInformation()}

        {paymentError && (
          <div className="p-3 mt-2 bg-red-200 rounded-sm">
            {t(
              "There was a problem processing your payment method. Please verify that your billing details are correct. If this problem persists, please contact us."
            )}
          </div>
        )}
      </DrawerContent>

      <DrawerActions>
        <div className="w-full grow">
          <Checkbox
            label={t("Set as default payment method")}
            subtitle={t("Default payment message")}
            className="text-base font-normal font-hanken"
            onChange={() =>
              setIsDefaultPaymentChecked(!isDefaultPaymentChecked)
            }
            checked={isDefaultPaymentChecked}
            data-testid="add_payment_method-default-checkbox"
            isDisabled={isFirstPaymentMethod}
          />
          <div className="flex items-end justify-end pr-2 mb-6 grow">
            <Button
              className="w-full py-5 font-hanken sm:w-56"
              variant={ButtonVariant.Primary}
              type="submit"
              isDisabled={stripe == null || isLoading}
              isLoading={isLoading}
            >
              {submitLabel ?? t("Add Payment Method")}
            </Button>
          </div>
        </div>
      </DrawerActions>
    </form>
  );
}
